/* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, useEffect} from 'react'
import {useMutation, useQueryClient} from 'react-query'
import {MenuComponent} from '../../../../../_metronic/assets/ts/components'
import {ID, KTIcon, QUERIES} from '../../../../../_metronic/helpers'
import {useListView} from '../../core/ListViewProvider'
import {useQueryResponse} from '../../core/QueryResponseProvider'
import {
  deleteClassifiedPost,
  approveClassifiedPost,
  rejectClassifiedPost,
  promoteClassifiedPost,
  restoreClassifiedPost,
} from '../../core/_requests'
import {ClassifiedPost} from '../../core/_models'
import {toast} from 'react-toastify'

type Props = {
  id: ID
  post: ClassifiedPost
  isDeleted: boolean
}

const ActionsCell: FC<Props> = ({id, post, isDeleted}) => {
  const {setItemIdForUpdate} = useListView()
  const {query} = useQueryResponse()
  const queryClient = useQueryClient()

  useEffect(() => {
    MenuComponent.reinitialization()
  }, [])

  const openEditModal = () => {
    setItemIdForUpdate(id)
  }

  const deleteItem = useMutation(() => deleteClassifiedPost(id), {
    onSuccess: () => {
      queryClient.invalidateQueries([`${QUERIES.CLASSIFIED_POSTS_LIST}-${query}`])
      toast.success('Post deleted successfully')
    },
    onError: () => {
      toast.error('Failed to delete post')
    },
  })

  const approveItem = useMutation(() => approveClassifiedPost(id), {
    onSuccess: () => {
      queryClient.invalidateQueries([`${QUERIES.CLASSIFIED_POSTS_LIST}-${query}`])
      toast.success('Post approved successfully')
    },
    onError: () => {
      toast.error('Failed to approve post')
    },
  })

  const rejectItem = useMutation(() => rejectClassifiedPost(id), {
    onSuccess: () => {
      queryClient.invalidateQueries([`${QUERIES.CLASSIFIED_POSTS_LIST}-${query}`])
      toast.success('Post rejected successfully')
    },
    onError: () => {
      toast.error('Failed to reject post')
    },
  })

  const promoteItem = useMutation(() => promoteClassifiedPost(id), {
    onSuccess: () => {
      queryClient.invalidateQueries([`${QUERIES.CLASSIFIED_POSTS_LIST}-${query}`])
      toast.success(post.isPromoted ? 'Post unpromoted' : 'Post promoted successfully')
    },
    onError: () => {
      toast.error('Failed to promote post')
    },
  })

  const restoreItem = useMutation(() => restoreClassifiedPost(id), {
    onSuccess: () => {
      queryClient.invalidateQueries([`${QUERIES.CLASSIFIED_POSTS_LIST}-${query}`])
      toast.success('Post restored successfully')
    },
    onError: () => {
      toast.error('Failed to restore post')
    },
  })

  return (
    <>
      <a
        href='#'
        className='btn btn-light btn-active-light-primary btn-sm'
        data-kt-menu-trigger='click'
        data-kt-menu-placement='bottom-end'
      >
        Actions
        <KTIcon iconName='down' className='fs-5 m-0' />
      </a>
      {/* begin::Menu */}
      <div
        className='menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-600 menu-state-bg-light-primary fw-bold fs-7 w-175px py-4'
        data-kt-menu='true'
      >
        {/* Edit */}
        {!isDeleted && (
          <div className='menu-item px-3'>
            <a className='menu-link px-3' onClick={openEditModal}>
              <KTIcon iconName='pencil' className='fs-6 me-2' />
              Edit
            </a>
          </div>
        )}

        {/* Approve/Reject */}
        {post.approvalStatus === 'Pending' && !isDeleted && (
          <>
            <div className='menu-item px-3'>
              <a
                className='menu-link px-3 text-success'
                onClick={async () => await approveItem.mutateAsync()}
              >
                <KTIcon iconName='check' className='fs-6 me-2' />
                Approve
              </a>
            </div>
            <div className='menu-item px-3'>
              <a
                className='menu-link px-3 text-danger'
                onClick={async () => await rejectItem.mutateAsync()}
              >
                <KTIcon iconName='cross' className='fs-6 me-2' />
                Reject
              </a>
            </div>
          </>
        )}

        {/* Promote */}
        {post.approvalStatus === 'Approved' && !isDeleted && (
          <div className='menu-item px-3'>
            <a className='menu-link px-3' onClick={async () => await promoteItem.mutateAsync()}>
              <KTIcon iconName='arrows-circle' className='fs-6 me-2' />
              {post.isPromoted ? 'Unpromote' : 'Promote'}
            </a>
          </div>
        )}

        {/* Delete */}
        <div className='separator my-2'></div>
        <div className='menu-item px-3'>
          <a
            className='menu-link px-3 text-danger'
            data-kt-users-table-filter='delete_row'
            onClick={async () => await deleteItem.mutateAsync()}
          >
            <KTIcon iconName='trash' className='fs-6 me-2' />
            Delete
          </a>
        </div>
        {isDeleted && (
          <div className='menu-item px-3'>
            <a
              className='menu-link px-3 text-primary'
              onClick={async () => await restoreItem.mutateAsync()}
            >
              <KTIcon iconName='redo' className='fs-6 me-2' />
              Restore
            </a>
          </div>
        )}
      </div>
      {/* end::Menu */}
    </>
  )
}

export {ActionsCell}
